import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getCallStatsThunk,
  getDashboardDataThunk,
} from "../actions/dashboard.action";

interface IChannelStats {
  totalTickets: number;
  totalMessages: number;
  inboundMessages: number;
  outboundMessages: number;
  closedTickets: number;
  failedTickets: number;
  needsReplyCount: number;
  openTickets: number;
}

interface ICampaignChannelSummary {
  count: number;
  sends: number;
}

interface ICampaignSummary {
  total: number;
  running: number;
  scheduled: number;
  finished: number;
  by_channel: {
    call?: ICampaignChannelSummary;
    sms?: ICampaignChannelSummary;
    whatsapp?: ICampaignChannelSummary;
    email?: ICampaignChannelSummary;
  };
}

interface IDashboardData {
  number_of_calls: number;
  total_minutes: number;
  total_seconds: number;
  completion_rate: number;
  number_of_campaigns: number;
  minutes_provisioned: number;
  provisioned_sms?: number;
  provisioned_whatsapp?: number;
  sms?: IChannelStats;
  whatsapp?: IChannelStats;
  email?: IChannelStats;
  campaigns?: ICampaignSummary;
  inbound: {
    count: number;
    completed?: number;
    answered?: number;
    failed?: number;
    in_progress?: number;
    completion_rate: number;
    answered_rate?: number;
  };
  outbound: {
    count: number;
    completed?: number;
    answered?: number;
    completion_rate: number;
    answered_rate?: number;
    need_review_count?: number;
    need_review_campaigns?: {
      _id: string;
      campaign_name: string;
      channel?: string;
      status?: string;
      need_review_count: number;
    }[];
  };
}

export interface DashboardState {
  data: IDashboardData;
  callStats: {
    label: string;
    count: number;
  }[];
  loading: boolean;
  inFlight: number;
  error: string | null;
}

// Initial state
const initialState: DashboardState = {
  data: {
    number_of_calls: 0,
    total_minutes: 0,
    total_seconds: 0,
    completion_rate: 0,
    number_of_campaigns: 0,
    minutes_provisioned: 0,
    provisioned_sms: 0,
    provisioned_whatsapp: 0,
    campaigns: {
      total: 0,
      running: 0,
      scheduled: 0,
      finished: 0,
      by_channel: {
        call: { count: 0, sends: 0 },
        sms: { count: 0, sends: 0 },
        whatsapp: { count: 0, sends: 0 },
        email: { count: 0, sends: 0 },
      },
    },
    inbound: {
      count: 0,
      completed: 0,
      answered: 0,
      failed: 0,
      in_progress: 0,
      completion_rate: 0,
      answered_rate: 0,
    },
    outbound: {
      count: 0,
      completed: 0,
      answered: 0,
      completion_rate: 0,
      answered_rate: 0,
      need_review_count: 0,
      need_review_campaigns: [],
    },
  },
  callStats: [],
  loading: false,
  inFlight: 0,
  error: null,
};

const beginLoad = (state: DashboardState) => {
  state.inFlight += 1;
  state.loading = true;
};

const endLoad = (state: DashboardState) => {
  state.inFlight = Math.max(0, state.inFlight - 1);
  state.loading = state.inFlight > 0;
};

// Dashboard slice
const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDashboardDataThunk.pending, beginLoad)
      .addCase(getDashboardDataThunk.fulfilled, (state, action) => {
        const next = action.payload?.data || {};
        state.data = {
          ...state.data,
          ...next,
          campaigns: next.campaigns || state.data.campaigns,
        };
        endLoad(state);
      })
      .addCase(getDashboardDataThunk.rejected, (state, action) => {
        state.error = action.payload as string;
        endLoad(state);
      });
    builder
      .addCase(getCallStatsThunk.pending, beginLoad)
      .addCase(getCallStatsThunk.fulfilled, (state, action) => {
        state.callStats = action.payload.data;
        endLoad(state);
      })
      .addCase(getCallStatsThunk.rejected, (state, action) => {
        state.error = action.payload as string;
        endLoad(state);
      });
  },
});

export const { clearError } = dashboardSlice.actions;
export default dashboardSlice.reducer;
